import { canOpenInBrowser } from '../nativeWebInteraction/nativeFeatureAvailability'
import { openUrlInBrowser } from '../nativeWebInteraction/nativeWebInteraction'
import { inspect, log } from './logger'
import router from '@/routes/router'
import { logEvent } from '@/utils/http-client'
import { appRoutePaths } from '@/routes/appRoutes'
import { FreeCoffeeInfoResponse, FreeCoffeeType, FreeStuffStatus, FreeStuffStatusInfo } from 'aven_types'
import store from '../store'
import { getFreeCoffeeInfo } from '../services/api'
import { i18n } from './i18n'

export interface FreeCoffeeAssetsAndCopyData {
    image: string
    freeCoffeeCopy: string
    buttonClasses: string
}

export const getFreeCoffeeAssetsAndCopy = (): FreeCoffeeAssetsAndCopyData => {
    const DUNKIN_AND_GIFT_CARD_IMAGE_PATH = require('@/assets/images/dunkin_sandwich_free_coffee_gift_card.png')
    const STARBUCKS_AND_GIFT_CARD_IMAGE_PATH = require('@/assets/images/starbucks_frap_with_gift_card_cropped.webp')

    const DUNKIN_ASSETS_AND_COPY: FreeCoffeeAssetsAndCopyData = {
        image: DUNKIN_AND_GIFT_CARD_IMAGE_PATH,
        freeCoffeeCopy: "Dunkin'",
        buttonClasses: 'tw-bg-[#EF6A00] tw-text-white tw-border-0',
    }

    const STARBUCKS_ASSETS_AND_COPY: FreeCoffeeAssetsAndCopyData = {
        image: STARBUCKS_AND_GIFT_CARD_IMAGE_PATH,
        freeCoffeeCopy: 'Starbucks',
        buttonClasses: 'tw-bg-[#243B34] tw-text-white tw-border-0',
    }

    const freeCoffeeTypeToAssetsAndCopyMap: Record<FreeCoffeeType, FreeCoffeeAssetsAndCopyData> = {
        [FreeCoffeeType.DUNKIN]: DUNKIN_ASSETS_AND_COPY,
        [FreeCoffeeType.STARBUCKS]: STARBUCKS_ASSETS_AND_COPY,
    }

    // Default information for users who have not been assigned any policu
    const DEFAULT_ASSETS_AND_COPY = STARBUCKS_ASSETS_AND_COPY

    if (!store.state.freeCoffeeSelectionByPolicy) {
        log.info('FreeCoffeeCard => promoAssetsAndCopy => user has not signed up for any promo - returning default assets and copy')
        return DEFAULT_ASSETS_AND_COPY
    }

    return freeCoffeeTypeToAssetsAndCopyMap[store.state.freeCoffeeSelectionByPolicy]
}

export function openGiftCardUrl(issuedGiftCardUrl: string) {
    if (canOpenInBrowser()) {
        return openUrlInBrowser(issuedGiftCardUrl)
    } else {
        window.open(issuedGiftCardUrl, '_blank')
    }
}

export function shareWithAFriendRedirect(buttonClickAnalyticsEvent: string) {
    logEvent(buttonClickAnalyticsEvent)
    log.info('FreeCoffeeCard => Clicked button, routing to referral send options')
    router.push({ path: appRoutePaths.REFERRAL_SEND_OPTIONS })
}

export function convertDtiFractionToPercentage(dtiFraction: number | null): string {
    if (dtiFraction === null) {
        return 'UNAVAILABLE'
    }
    const dtiPercentage = dtiFraction * 100
    return dtiPercentage.toFixed(0)
}

export function areAllReportsCompletedInFreeCoffeeInfo(freeCoffeeInfo: FreeCoffeeInfoResponse): boolean {
    let allReportsCompleted = false
    if (!freeCoffeeInfo.dtiInfo.plaidAssetReportPending) {
        allReportsCompleted = true
    }
    return allReportsCompleted
}

export async function pollForFreeCoffeeInfoReportsCompleted() {
    const MAX_NUMBER_OF_POLLING_ATTEMPTS_PER_CALL = 50
    const DELAY_BETWEEN_POLLING_ATTEMPTS_MS = 3000

    if (store.state.isPollingForFreeCoffeeInfoReportCompeletion) {
        log.info('pollForFreeCoffeeInfoReportsCompleted => Already polling for free coffee info reports completion, not starting new poll')
        return
    }

    store.commit('setIsPollingForFreeCoffeeInfoReportCompeletion', { isPolling: true })
    log.info('pollForFreeCoffeeInfoReportsCompleted => Starting poll for free coffee info reports completion')

    for (let numberOfAttempts = 0; numberOfAttempts < MAX_NUMBER_OF_POLLING_ATTEMPTS_PER_CALL; numberOfAttempts++) {
        log.info('maybePollForFreeCoffeeInfoPlaidAssetReportCompletion => Polling for plaid asset report completion attempt %{numberOfAttempts}', { numberOfAttempts })
        const response = await getFreeCoffeeInfo()
        store.commit('setFreeCoffeeInfo', response?.data.payload)
        log.info('pollForFreeCoffeeInfoReportsCompleted => retrieved and commited free coffee info')

        if (store.state.freeCoffeeInfo ? areAllReportsCompletedInFreeCoffeeInfo(store.state.freeCoffeeInfo) : false) {
            log.info('maybePollForFreeCoffeeInfoPlaidAssetReportCompletion => All reports completed, stopping poll')
            break
        }

        // Don't wait after last attempt
        if (numberOfAttempts < MAX_NUMBER_OF_POLLING_ATTEMPTS_PER_CALL - 1) {
            await new Promise((resolve) => setTimeout(resolve, DELAY_BETWEEN_POLLING_ATTEMPTS_MS))
        }
    }
    store.commit('setIsPollingForFreeCoffeeInfoReportCompeletion', { isPolling: false })
}

export function getFreeCoffeeCardPolicifiedDescription(
    freeStuffStatusInfo: FreeStuffStatusInfo | null,
    freeCoffeeAssetsAndCopy: FreeCoffeeAssetsAndCopyData,
    upsellFlowAvailableOrInUpsellFlow: boolean
): string {
    log.info(
        'getFreeCoffeeCardPolicifiedDescription => status: %{status}, freeCoffeeAssetsAndCopy: %{freeCoffeeAssetsAndCopyInspect}, upsellFlowAvailableOrInUpsellFlow: %{upsellFlowAvailableOrInUpsellFlow}',
        { status: freeStuffStatusInfo?.status, freeCoffeeAssetsAndCopyInspect: inspect(freeCoffeeAssetsAndCopy), upsellFlowAvailableOrInUpsellFlow }
    )

    let description = ''

    // Connect Plaid Description
    if (freeStuffStatusInfo?.status === FreeStuffStatus.FREE_COFFEE_PLAID_NOT_CONNECTED || freeStuffStatusInfo?.status === FreeStuffStatus.FREE_COFFEE_CONNECT_PLAID_AGAIN) {
        log.info('getFreeCoffeeCardPolicifiedDescription => Connecting Plaid')
        description = i18n.t('components.FreeCoffeeCardPolicified.freeCoffeeConnectPlaidContent.genericConnectPlaid') as string
    }

    // Connect Plaid to Verify Income
    if (freeStuffStatusInfo?.status === FreeStuffStatus.FREE_COFFEE_CONNECT_PLAID_TO_VERIFY_INCOME) {
        log.info('getFreeCoffeeCardPolicifiedDescription => Connecting Plaid to Verify Income')
        description = i18n.t('components.FreeCoffeeCardPolicified.freeCoffeeConnectPlaidContent.connectPlaidToVerifyIncome', {
            grossAnnualIncomeThreshold: freeStuffStatusInfo.content.grossAnnualIncomeThreshold,
        }) as string
    }

    // Connect Plaid Again to Verify Income
    if (freeStuffStatusInfo?.status === FreeStuffStatus.FREE_COFFEE_CONNECT_PLAID_AGAIN_TO_VERIFY_INCOME) {
        log.info('getFreeCoffeeCardPolicifiedDescription => Connecting Plaid Again to Verify Income')
        description = i18n.t('components.FreeCoffeeCardPolicified.freeCoffeeConnectPlaidContent.connectPlaidAgainToVerifyIncome') as string
    }

    // Get Coffee Description (standard)
    if (freeStuffStatusInfo?.status === FreeStuffStatus.FREE_COFFEE_CAN_CLAIM_NOW && !upsellFlowAvailableOrInUpsellFlow) {
        log.info('getFreeCoffeeCardPolicifiedDescription => Can Claim Now')
        description = i18n.t('components.FreeCoffeeCardPolicified.freeCoffeeGetCoffeeContent.canClaimNow', {
            freeCoffeeCopy: freeCoffeeAssetsAndCopy.freeCoffeeCopy,
            vantageScoreThreshold: freeStuffStatusInfo.content.vantageScoreThreshold,
            vantageScore: freeStuffStatusInfo.content.vantageScore || '',
        }) as string
    }

    // Get Coffee with Weeks Left (standard)
    if (freeStuffStatusInfo?.status === FreeStuffStatus.FREE_COFFEE_CAN_CLAIM_NOW_WITH_WEEKS_LEFT && !upsellFlowAvailableOrInUpsellFlow) {
        log.info('getFreeCoffeeCardPolicifiedDescription => Can Claim Now With Weeks Left')
        description = i18n.t('components.FreeCoffeeCardPolicified.freeCoffeeGetCoffeeContent.canClaimNowWithWeeksLeft', {
            freeCoffeeCopy: freeCoffeeAssetsAndCopy.freeCoffeeCopy,
            weeksOfFreeCoffeeLeft: freeStuffStatusInfo.content.weeksOfFreeCoffeeLeft || '',
            remainingTimePeriodNoun: freeStuffStatusInfo.content.weeksOfFreeCoffeeLeft === 1 ? 'week' : 'weeks',
        }) as string
    }

    // Get Coffee Description (upsell)
    if (freeStuffStatusInfo?.status === FreeStuffStatus.FREE_COFFEE_CAN_CLAIM_NOW && upsellFlowAvailableOrInUpsellFlow) {
        log.info('getFreeCoffeeCardPolicifiedDescription => Can Claim Now (Upsell)')
        description = i18n.t('components.FreeCoffeeCardPolicified.freeCoffeeGetCoffeeContent.canClaimNow', {
            freeCoffeeCopy: freeCoffeeAssetsAndCopy.freeCoffeeCopy,
            vantageScoreThreshold: freeStuffStatusInfo.content.vantageScoreThreshold,
            vantageScore: freeStuffStatusInfo.content.vantageScore || '',
        }) as string
    }

    // Get Coffee with Weeks Left (upsell)
    if (freeStuffStatusInfo?.status === FreeStuffStatus.FREE_COFFEE_CAN_CLAIM_NOW_WITH_WEEKS_LEFT && upsellFlowAvailableOrInUpsellFlow) {
        log.info('getFreeCoffeeCardPolicifiedDescription => Can Claim Now With Weeks Left (Upsell)')
        description = i18n.t('components.FreeCoffeeCardPolicified.freeCoffeeGetCoffeeContent.canClaimNowWithWeeksLeft', {
            freeCoffeeCopy: freeCoffeeAssetsAndCopy.freeCoffeeCopy,
            weeksOfFreeCoffeeLeft: freeStuffStatusInfo.content.weeksOfFreeCoffeeLeft || '',
            remainingTimePeriodNoun: freeStuffStatusInfo.content.weeksOfFreeCoffeeLeft === 1 ? 'week' : 'weeks',
        }) as string
    }

    // Claiming Too Soon
    if (freeStuffStatusInfo?.status === FreeStuffStatus.FREE_COFFEE_CLAIMING_TOO_SOON) {
        log.info('getFreeCoffeeCardPolicifiedDescription => Claiming Too Soon')
        description = i18n.t('components.FreeCoffeeCardPolicified.freeCoffeeCountdownContent.claimingTooSoon', {
            freeCoffeeCopy: freeCoffeeAssetsAndCopy.freeCoffeeCopy,
            vantageScoreThreshold: freeStuffStatusInfo.content.vantageScoreThreshold,
            vantageScore: freeStuffStatusInfo.content.vantageScore || '',
        }) as string
    }

    // Claiming Too Soon with Weeks Left
    if (freeStuffStatusInfo?.status === FreeStuffStatus.FREE_COFFEE_CLAIMING_TOO_SOON_WITH_WEEKS_LEFT) {
        log.info('getFreeCoffeeCardPolicifiedDescription => Claiming Too Soon With Weeks Left')
        description = i18n.t('components.FreeCoffeeCardPolicified.freeCoffeeCountdownContent.claimingTooSoonWithWeeksLeft', {
            freeCoffeeCopy: freeCoffeeAssetsAndCopy.freeCoffeeCopy,
            weeksOfFreeCoffeeLeft: freeStuffStatusInfo.content.weeksOfFreeCoffeeLeft || '',
            remainingTimePeriodNoun: freeStuffStatusInfo.content.weeksOfFreeCoffeeLeft === 1 ? 'week' : 'weeks',
        }) as string
    }

    log.info('getFreeCoffeeCardPolicifiedDescription => description: %{description}', { description })
    return description
}
